import React, { useMemo, useState } from "react";
import { Container, LoginButton } from "../../styles/Styles";
import GuideNav from "./GuideNav";
import { useQuery } from "@apollo/client";
import {
    GET_GUIDE_INVOICE_SUBMISSION_PERIOD,
    GET_MY_GUIDE_INVOICES,
    IS_GUIDE_INVOICE_SUBMISSION_OPEN,
} from "../gql/guide-invoices-gql";
import ReviewMonthCalendar from "../common/ReviewMonthCalendar";
import GuideInvoiceRow from "../common/GuideInvoiceRow";
import { uploadGuideInvoice } from "../common/uploadGuideInvoice";
import { GuideInvoice } from "../common/interfaces";
import Loader from "../common/Loader";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { Roles } from "../common/roles";
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import moment from "moment/moment";

const Section = styled.div`
    background: white;
    padding: 1.5rem;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;
`;

const SubmissionInfo = styled.div`
    margin-bottom: 1rem;
    padding: 1rem;
    background: #f5f5f5;
    border-radius: 5px;
    color: #666;
`;

const FormGroup = styled.div`
    margin-bottom: 1rem;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
`;

const Input = styled.input`
    width: 100%;
    max-width: 400px;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
`;

const MonthLabel = styled.h3`
    margin: 0 0 1rem 0;
    color: #333;
`;

const InvoiceList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const getUploadErrorMessage = (error: any): string => {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;

    if (status === 403) {
        return "Invoice submission is currently closed";
    }
    if (status === 409) {
        return "Invoice already submitted for this period";
    }

    return message || error?.message || "Failed to upload invoice";
};

const GuideFinance = () => {
    const { user, isAuthenticated } = useAuth();
    const [currentMonth, setCurrentMonth] = useState(() => new Date());
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileInputKey, setFileInputKey] = useState(0);
    const [uploading, setUploading] = useState(false);

    const { data: submissionOpenData, loading: submissionOpenLoading } = useQuery(
        IS_GUIDE_INVOICE_SUBMISSION_OPEN,
        { skip: !isAuthenticated }
    );

    const isSubmissionOpen = submissionOpenData?.isGuideInvoiceSubmissionOpen === true;

    const { data: periodData, loading: periodLoading } = useQuery(
        GET_GUIDE_INVOICE_SUBMISSION_PERIOD,
        { skip: !isAuthenticated || !isSubmissionOpen }
    );

    const { data, loading, refetch } = useQuery(GET_MY_GUIDE_INVOICES, {
        skip: !isAuthenticated,
    });

    const submissionPeriod = periodData?.getGuideInvoiceSubmissionPeriod;

    const filteredInvoices = useMemo(() => {
        const invoices: GuideInvoice[] = data?.getMyGuideInvoices || [];
        const month = currentMonth.getMonth() + 1;
        const year = currentMonth.getFullYear();
        return invoices.filter(
            (invoice) => invoice.month === month && invoice.year === year
        );
    }, [data?.getMyGuideInvoices, currentMonth]);

    const currentMonthName = moment().format("MMMM");
    const nextMonthName = moment().add(1, "month").format("MMMM");

    const handleUpload = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!selectedFile) {
            toast.warning("Please select a PDF file to upload");
            return;
        }

        if (selectedFile.type !== "application/pdf") {
            toast.warning("Only PDF files are allowed");
            return;
        }

        setUploading(true);

        try {
            const result = await uploadGuideInvoice(selectedFile);
            toast.success("Invoice uploaded successfully!");
            setSelectedFile(null);
            setFileInputKey((prev) => prev + 1);

            const uploadedDate = new Date(result.year, result.month - 1, 1);
            setCurrentMonth(uploadedDate);
            await refetch();
        } catch (error: any) {
            toast.error(getUploadErrorMessage(error));
        } finally {
            setUploading(false);
        }
    };

    if (!isAuthenticated || user?.role[0] !== Roles.GUIDE) {
        return <Navigate to="/" />;
    }

    if (submissionOpenLoading || loading || (isSubmissionOpen && periodLoading)) {
        return <Loader />;
    }

    return (
        <Container>
            <GuideNav />
            <h2>Finance</h2>

            <Section>
                <h3>Invoice Submission</h3>
                {isSubmissionOpen && submissionPeriod ? (
                    <>
                        <SubmissionInfo>
                            Submit invoice for{" "}
                            <strong>
                                {moment()
                                    .month(submissionPeriod.month - 1)
                                    .year(submissionPeriod.year)
                                    .format("MMMM YYYY")}
                            </strong>
                        </SubmissionInfo>
                        <form onSubmit={handleUpload}>
                            <FormGroup>
                                <Label htmlFor="invoiceFile">PDF Invoice</Label>
                                <Input
                                    key={fileInputKey}
                                    id="invoiceFile"
                                    type="file"
                                    accept="application/pdf"
                                    onChange={(e) =>
                                        setSelectedFile(e.target.files?.[0] ?? null)
                                    }
                                />
                            </FormGroup>
                            <LoginButton type="submit" disabled={uploading}>
                                {uploading ? "Uploading..." : "Upload Invoice"}
                            </LoginButton>
                        </form>
                    </>
                ) : (
                    <SubmissionInfo>
                        The form to submit invoices will be available from the 30th of{" "}
                        {currentMonthName} till the 10th of {nextMonthName}.
                    </SubmissionInfo>
                )}
            </Section>

            <ReviewMonthCalendar
                currentDate={currentMonth}
                onMonthChange={setCurrentMonth}
            />

            <Section>
                <MonthLabel>
                    Invoices for {moment(currentMonth).format("MMMM YYYY")}
                </MonthLabel>
                {filteredInvoices.length === 0 ? (
                    <p>No invoices for this month.</p>
                ) : (
                    <InvoiceList>
                        {filteredInvoices.map((invoice) => (
                            <GuideInvoiceRow key={invoice._id} invoice={invoice} />
                        ))}
                    </InvoiceList>
                )}
            </Section>
        </Container>
    );
};

export default GuideFinance;
